import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Badges from "@/components/Badges";

const PLAN_NAMES = {
  coaching_1sem_monthly: "Coaching · 1 séance/sem",
  coaching_2sem_monthly: "Coaching · 2 séances/sem",
  libre_1sem_monthly: "Accès libre · 1 séance/sem",
  libre_2sem_monthly: "Accès libre · 2 séances/sem",
};

function fmtDate(iso, coaching = false) {
  const d = new Date(iso);
  const base = d.toLocaleString("fr-FR", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" });
  if (!coaching) return base;
  // Shift displayed time by +15 min to reflect the sport-start time
  const shifted = new Date(d.getTime() + 15 * 60 * 1000);
  return shifted.toLocaleString("fr-FR", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" });
}

export default function MySpace() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);

  const loadBookings = () => api.get("/bookings/me").then((r) => setBookings(r.data)).catch(() => {});

  useEffect(() => { loadBookings(); }, []);

  const cancel = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Réservation annulée");
      loadBookings();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  if (!user || user === false) return null;
  const hasPlan = !!user.active_plan;
  const trialAvailable = !user.trial_used && user.trial_available;
  const isCoaching = (hasPlan && user.active_plan.startsWith("coaching_")) || trialAvailable;

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      <Nav />
      <div className="mx-auto max-w-6xl px-6 py-16 lg:px-12">
        <p className="mb-3 text-xs font-medium uppercase tracking-[0.32em] text-stone-500">Espace membre</p>
        <h1 className="font-display mb-2 text-5xl font-light tracking-tight text-stone-900">Bonjour {user.name}</h1>
        <p className="mb-14 text-base font-light text-stone-500">Gérez votre abonnement et vos réservations.</p>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Plan */}
          <div className="rounded-2xl border border-stone-200 bg-white p-8">
            <p className="text-xs uppercase tracking-[0.24em] text-stone-500">Forfait actuel</p>
            {hasPlan ? (
              <>
                <p data-testid="my-plan" className="font-display mt-3 text-xl text-stone-900">{PLAN_NAMES[user.active_plan] || user.active_plan}</p>
                <p className="mt-2 text-sm text-stone-500">{user.sessions_per_week} séance(s) / semaine incluse(s)</p>
              </>
            ) : (
              <>
                <p data-testid="my-plan-none" className="font-display mt-3 text-xl text-stone-900">Aucun forfait actif</p>
                <Button data-testid="cta-choose-plan" onClick={() => nav("/#plans")} className="mt-5 rounded-full bg-stone-900 px-6 hover:bg-stone-800">Choisir un forfait</Button>
              </>
            )}
          </div>

          {/* Book */}
          <div className="rounded-2xl border border-stone-900 bg-stone-900 p-8 text-stone-50">
            <p className="text-xs uppercase tracking-[0.24em] text-stone-400">Réservation</p>
            <p className="font-display mt-3 text-xl">Prochain créneau</p>
            <p className="mt-2 text-sm text-stone-400">Lun-Ven, 9h → 18h.</p>
            <Button data-testid="cta-book" onClick={() => nav("/reserver")} className="mt-6 w-full rounded-full bg-[#8B7D6B] py-5 hover:bg-[#7A6C5B]">
              Réserver un créneau
            </Button>
          </div>
        </div>

        {/* Bookings list */}
        <div className="mt-16">
          <div className="mb-6 flex items-baseline justify-between">
            <h2 className="font-display text-2xl font-light tracking-tight text-stone-900">Ma progression</h2>
            <p className="text-xs uppercase tracking-[0.2em] text-stone-500">
              {bookings.filter((b) => b.status !== "cancelled").length} séance(s)
            </p>
          </div>
          <Badges count={bookings.filter((b) => b.status !== "cancelled").length} />
        </div>

        <div className="mt-16">
          <div className="mb-6 flex items-baseline justify-between">
            <h2 className="font-display text-2xl font-light tracking-tight text-stone-900">Mes réservations</h2>
            <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{bookings.length} total</p>
          </div>
          <div className="rounded-2xl border border-stone-200 bg-white">
            {bookings.length === 0 && (
              <p data-testid="bookings-empty" className="p-8 text-sm text-stone-500">Aucune réservation pour le moment.</p>
            )}
            {bookings.map((b, i) => (
              <div key={b.id} data-testid={`booking-row-${i}`} className={`flex items-center justify-between p-6 ${i > 0 ? "border-t border-stone-100" : ""}`}>
                <div>
                  <p className="text-base capitalize text-stone-900">{fmtDate(b.slot_start, isCoaching || b.is_trial)}</p>
                  <div className="mt-1 flex items-center gap-3 text-xs uppercase tracking-wider text-stone-500">
                    <span>{b.status === "cancelled" ? "Annulée" : "Confirmée"}</span>
                    {b.is_trial && <span className="rounded-full bg-[#8B7D6B]/10 px-2 py-0.5 text-[#8B7D6B]">Essai</span>}
                  </div>
                </div>
                {b.status !== "cancelled" && new Date(b.slot_start) > new Date() && (
                  <Button data-testid={`booking-cancel-${i}`} variant="ghost" onClick={() => cancel(b.id)} className="text-sm text-stone-500 hover:text-red-600">
                    Annuler
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
